import { Column } from '@tanstack/react-table';
import { FormAction } from '@anytag/shared-types';
import { useDownloadFile } from '@anytag/shared-hooks';
import { CampaignPostSortField } from '@anytag/graphql';
import { getAdvancedSearchApiUrl } from '@anytag/shared-utils';
import { DownloadCsvPagesAndRowsModalValues, getDownloadCSVRangeRows } from '@anytag/shared-molecules';
import { useCampaignPostsDiscoveryFilter } from '../../CampaignPostsDiscoveryFilter';
import { CampaignPostsDiscoveryRowData } from '../types';

interface Props {
  onClose?: () => void;
  visibleColumns?: Column<CampaignPostsDiscoveryRowData>[];
}

export const useDownloadCampaignPostsDiscovery = ({ onClose, visibleColumns }: Props) => {
  const { downloading, handleDownloadFile } = useDownloadFile();
  const {
    sort,
    limit,
    filter: {
      keyword,
      endDate,
      startDate,
      campaigns,
      countryIds,
      categoryIds,
      campaignTypes,
      advertiserIds,
      influencerIds,
      socialPostTypes
    }
  } = useCampaignPostsDiscoveryFilter();

  const handleDownloadCsv: FormAction<DownloadCsvPagesAndRowsModalValues>['onSubmit'] = async (formValues) => {
    const { firstRow, lastRow } = getDownloadCSVRangeRows(formValues);
    const filteredColumns = visibleColumns?.reduce<CampaignPostSortField[]>(
      (acc, curr) =>
        curr.columnDef.meta?.sortField ? [...acc, curr.columnDef.meta.sortField as CampaignPostSortField] : acc,
      []
    );

    await handleDownloadFile({
      filenamePrefix: `post_discovery_csv_${new Date().toUTCString()}`,
      method: 'GET',
      url: getAdvancedSearchApiUrl('api/campaign_post/csv'),
      params: {
        firstRow,
        lastRow,
        keyword,
        campaigns,
        countryIds,
        categoryIds,
        campaignTypes,
        advertiserIds,
        influencerIds,
        socialPostTypes,
        maxPostDate: endDate,
        minPostDate: startDate,
        orderByField: sort.field,
        orderByOrder: sort.order,
        selectedColumns: filteredColumns
      }
    });

    onClose?.();
  };

  return { limit, downloading, handleDownloadCsv };
};
